import { Zone, ZoneType } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colorToCss, FALLBACK_ZONE_COLOR } from "../utils/zoneColor";
import { filterZones, ZONE_LIST_LIMIT } from "../utils/zoneList";
import { zoneTypeId } from "../utils/zoneTypeId";
import { zoneTypeLabel } from "../utils/zoneTypes";
import ColorDot from "./ColorDot";

interface Props {
  zones: Zone[];
  types: ZoneType[];
  loading: boolean;
  truncated: boolean;
  selectedZoneId?: string;
  typeFilter: string | null;
  onTypeFilterChange(typeId: string | null): void;
  onSelect(id: string): void;
}

export default function ZoneList({
  zones,
  types,
  loading,
  truncated,
  selectedZoneId,
  typeFilter,
  onTypeFilterChange,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => filterZones(zones, search, typeFilter), [zones, search, typeFilter]);
  const typeLabels = useMemo(() => new Map(types.map(type => [type.id, zoneTypeLabel(type, t)])), [types, t]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Stack spacing={1} sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder={t("zonesPage.list.search")}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Autocomplete<ZoneType>
          size="small"
          options={types}
          value={types.find(type => type.id === typeFilter) ?? null}
          getOptionLabel={type => typeLabels.get(type.id) ?? type.id}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, value) => onTypeFilterChange(value?.id ?? null)}
          renderInput={params => <TextField {...params} placeholder={t("zonesPage.list.allTypes")} />}
        />
      </Stack>
      {loading && <LinearProgress />}
      {truncated && (
        <Alert severity="warning" sx={{ mx: 1 }}>
          {t("zonesPage.list.truncated")}
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
        {t("zonesPage.list.count", { count: filtered.length })}
      </Typography>
      <List dense sx={{ overflowY: "auto", flex: 1 }}>
        {filtered.slice(0, ZONE_LIST_LIMIT).map(zone => (
          <ListItemButton key={zone.id} selected={zone.id === selectedZoneId} onClick={() => onSelect(zone.id)}>
            <ColorDot color={colorToCss(zone.fillColor ?? FALLBACK_ZONE_COLOR)} />
            <ListItemText
              primary={zone.name}
              secondary={(zone.zoneTypes ?? [])
                .map(type => typeLabels.get(zoneTypeId(type) ?? "") ?? zoneTypeId(type))
                .join(", ")}
            />
          </ListItemButton>
        ))}
      </List>
      {filtered.length > ZONE_LIST_LIMIT && (
        <Typography variant="caption" color="text.secondary" sx={{ p: 1, px: 2 }}>
          {t("zonesPage.list.moreHits", { count: ZONE_LIST_LIMIT })}
        </Typography>
      )}
    </Box>
  );
}
